/**
 * Phase 5 Day Editor — rewrites the meal-plan editor for the new shape.
 *
 * Owns state for one voyage day's meal plan AND for the voyage-level
 * `targets[]` (which lives on the voyage doc, not the day). The Save button
 * fans out:
 *    - PUT /voyages/{id}                  → header (targets)
 *    - PUT /voyages/{id}/days/{n}         → arrival_time/departure_time
 *    - PUT /voyages/{id}/days/{n}/meal-plan → the full new MealPlan body
 *
 * Sections, top → bottom:
 *    1. DayNavigator + Export PDF button (unchanged)
 *    2. Sticky day header card (date / port / arrival / departure / status)
 *    3. NEW Targets panel (voyage-level)
 *    4. NEW Regular Restaurants table (open/closed + menus + dishes + per-row chef's special)
 *    5. NEW Specialty Restaurants table (per-service rows, brunch activation)
 *    6. OVC block — restructured with per-meal theme
 *    7. Crew Mess block — restructured with per-meal theme
 *    8. Events table (auto-sorted by start_time)
 *    9. Special Events picker (now with per-event Extras)
 * Top-level Chef's Special section is GONE — folded into row #4.
 */
import { useState, useEffect, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Loader2, Save, RotateCcw, RefreshCw, Download, FileText, LayoutGrid, ChevronDown, ChevronRight,
} from "lucide-react";
import { toast } from "@/hooks/use-toast";

import {
  getDay, patchDay, putMealPlan, eventsFromDefaults,
  getFullMealPlan,
  listRestaurants, listThemeBuffets, listLiveStations, listStandardStations,
  listBuffetMaterials,
  listCrewMessLiveStations,
  listCrewMessThemes, listChefsSpecials, listSpecialEvents,
  listTargets, listFestivals, syncMealPlanFromLibrary,
  // Phase 15 (F3) — duplicate Live Station usage detector.
  listVoyageLiveStationUsage,
  // Phase 16 (F8) — duplicate menu rotation usage detector.
  listVoyageMenuUsage,
  // Phase 16 (CO1/CO4) — Pizza catalog, Pizza sets, Bakery specials,
  // Chef's/Bakery special groups. All used by the meal-plan editor
  // lib-load fan-out below. MISSING from this import block in the
  // initial Phase 16 ship caused a `ReferenceError: listPizzas is not
  // defined` crash on opening the day editor (webpack didn't catch it
  // because JS lets free variables compile clean).
  listPizzas, listPizzaSets, listBakerySpecials,
  listChefsSpecialsGroups, listBakerySpecialsGroups,
  // Phase 16 (F13) — Carving catalog + saved bundles (mirror of Pizza).
  listCarvings, listCarvingSets,
} from "@/api/mealPlanApi";
import { updateVoyage } from "@/lib/voyagesApi";

import DayNavigator from "@/components/mealplan/DayNavigator";
import RegularRestaurantsTable from "@/components/mealplan/RegularRestaurantsTable";
import SpecialtyRestaurantsTable from "@/components/mealplan/SpecialtyRestaurantsTable";
import BuffetsBlock from "@/components/mealplan/BuffetsBlock";
import BuffetDayPrintButtons from "@/components/mealplan/BuffetDayPrintButtons";
import CrewMessBlock from "@/components/mealplan/CrewMessBlock";
// Phase 7+/Crew-Mess-Buffet — render crew_mess through OvcBlock so its
// 3-meal × Hot/Cold/Pastry/Lines/LiveStation UX is identical to OVC.
import OvcBlock from "@/components/mealplan/OvcBlock";
// Phase 16 — section-scoped error boundary (BLOCKER 1 mitigation).
import SectionErrorBoundary from "@/components/SectionErrorBoundary";
import EventsTable from "@/components/mealplan/EventsTable";
import SpecialEventsPicker from "@/components/mealplan/SpecialEventsPicker";
import CanapeFunctionsBlock from "@/components/mealplan/CanapeFunctionsBlock";
// Hotfix 4 — collapsible top-level sections in the day editor.
import CollapsibleSection from "@/components/mealplan/CollapsibleSection";
import {
  computeMenuDuplicates,
  daysWithDuplicateMenus,
} from "@/components/mealplan/menuDuplicates";
import {
  computeChefSpecialDuplicates,
  daysWithDuplicateChefSpecials,
} from "@/components/mealplan/chefSpecialDuplicates";
import {
  sanitizeMealPlanForSave,
  formatValidationError,
} from "@/components/mealplan/mealPlanSaveHelpers";

const EMPTY_MEAL_PLAN = {
  regular_restaurants: [],
  specialty_restaurants: [],
  buffets: [],
  crew_mess: null,
  events: [],
  special_events: [],
};

function SectionTitle({ children, accent = "slate" }) {
  const cls =
    accent === "navy"
      ? "border-l-4 border-l-[#0B3D5C] bg-slate-100 text-[#0B3D5C]"
      : accent === "amber"
        ? "border-l-4 border-l-amber-500 bg-amber-50 text-amber-900"
        : "border-l-4 border-l-slate-400 bg-slate-100 text-slate-800";
  return (
    <div
      className={`mb-2 inline-flex items-center rounded-r-md px-3 py-1 text-[11px] font-bold uppercase tracking-widest ${cls}`}
    >
      {children}
    </div>
  );
}

function computeStatus(mp) {
  if (!mp)
    return { label: "empty", color: "bg-gray-100 text-gray-600 border-gray-200" };
  const regularFilled = (mp.regular_restaurants || []).some(
    (r) =>
      (r.lunch_dishes && r.lunch_dishes.length) ||
      (r.dinner_dishes && r.dinner_dishes.length) ||
      r.lunch_menu_name ||
      r.dinner_menu_name ||
      (r.chefs_special && r.chefs_special.enabled)
  );
  const specialtyFilled = (mp.specialty_restaurants || []).some((sr) =>
    (sr.services || []).some((s) => s.open)
  );
  const ovcFilled =
    !!mp.ovc &&
    ["breakfast", "lunch", "dinner"].some((m) => {
      const b = mp.ovc[m] || {};
      return (
        (b.hot_kitchen || []).length ||
        (b.cold_kitchen || []).length ||
        (b.pastry || []).length ||
        b.theme_buffet_id ||
        b.theme_buffet_name
      );
    });
  const crewFilled =
    !!mp.crew_mess &&
    ["breakfast", "lunch", "dinner"].some((m) => {
      const b = mp.crew_mess[m] || {};
      // Phase 7+/Crew-Mess-Buffet — crew_mess now mirrors OVC shape.
      return (
        (b.hot_kitchen || []).length ||
        (b.cold_kitchen || []).length ||
        (b.pastry || []).length ||
        (b.lines || []).length ||
        (b.live_station && b.live_station.enabled) ||
        b.theme_buffet_id ||
        b.theme_buffet_name
      );
    });
  const seFilled = (mp.special_events || []).length > 0;

  const filled = [regularFilled, specialtyFilled, ovcFilled, crewFilled, seFilled].filter(
    Boolean
  ).length;
  if (filled === 0)
    return { label: "empty", color: "bg-gray-100 text-gray-600 border-gray-200" };
  if (filled >= 4)
    return {
      label: "complete",
      color: "bg-emerald-100 text-emerald-800 border-emerald-200",
    };
  return { label: "partial", color: "bg-amber-50 text-amber-700 border-amber-200" };
}

export default function MealPlanEditor({ voyage, onExportPdf, exporting }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedDay = parseInt(searchParams.get("day"), 10) || 1;
  const safeDay = Math.min(Math.max(requestedDay, 1), voyage.total_days || 1);

  // Library lists (loaded once per editor mount).
  const [libs, setLibs] = useState({
    restaurants: [], theme_buffets: [], live_stations: [], standard_stations: [],
    // Phase 7++++ — buffet materials drive the per-dish material picker
    // inside hot/cold/pastry stations + live stations + buffet lines.
    buffet_materials: [],
    // Phase 7+/Crew-Mess-Buffet — parallel library for the Crew Mess Buffet
    // Live Station picker (separate from the OVC live_stations).
    crew_mess_live_stations: [],
    crew_mess_themes: [], chefs_specials: [], special_events: [],
    targets: [], festivals: [],
  });
  const [libsLoaded, setLibsLoaded] = useState(false);
  // Phase 7+++ — sync-from-library button + auto-sync state.
  const [syncing, setSyncing] = useState(false);
  const [autoSynced, setAutoSynced] = useState(false);

  // Active day + meal_plan state.
  const [day, setDay] = useState(null);
  const [arrivalTime, setArrivalTime] = useState("");
  const [departureTime, setDepartureTime] = useState("");
  const [mealPlan, setMealPlan] = useState(EMPTY_MEAL_PLAN);
  // Voyage-level targets — saved via PUT /voyages/{id}, NOT the meal-plan route.
  const [targets, setTargets] = useState(voyage.targets || []);
  const [originalSnapshot, setOriginalSnapshot] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);

  // Phase-5 refinement 5 — full voyage meal-plan cache (every day with its
  // saved meal_plan snapshot). Used to detect duplicate menu rotations
  // across days for the SAME restaurant + side. Refreshed on mount + after
  // every save.
  const [fullMealPlan, setFullMealPlan] = useState([]);
  // Phase 14 — buffet lines / islands for the voyage's ship.
  const [linesLib, setLinesLib] = useState([]);

  const refreshFullMealPlan = useCallback(async () => {
    try {
      const data = await getFullMealPlan(voyage.id);
      // Accept either {days: [...]} or [...] depending on backend shape.
      const days = Array.isArray(data) ? data : data?.days || [];
      setFullMealPlan(days);
    } catch (e) {
      // Soft failure — duplicate highlighting just becomes a no-op.
      // eslint-disable-next-line no-console
      console.warn("Failed to load full voyage meal plan", e);
    }
  }, [voyage.id]);

  // Phase 7+++ — sync the meal_plan structure with the current library
  // state. Appends rows for any newly enabled lib_restaurants; never
  // removes existing rows (stale lib refs surface as amber).
  const runSync = useCallback(
    async ({ silent } = {}) => {
      setSyncing(true);
      try {
        const res = await syncMealPlanFromLibrary(voyage.id);
        const total = res?.total_added || 0;
        if (total > 0) {
          // Re-pull current day so newly synced rows show up.
          await refreshFullMealPlan();
          await loadDay(safeDay);
          toast({
            title: "Synced from library",
            description:
              `${total} restaurant${total === 1 ? "" : "s"} added across the voyage` +
              ` (${res.added.regular} regular · ${res.added.specialty} specialty · ${res.added.buffet} buffet)`,
          });
        } else if (!silent) {
          toast({
            title: "Library already in sync",
            description: "No new restaurants to add.",
          });
        }
        return total;
      } catch (e) {
        toast({
          title: "Sync failed",
          description: e?.response?.data?.detail || e.message,
          variant: "destructive",
        });
        return 0;
      } finally {
        setSyncing(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [voyage.id, refreshFullMealPlan],
  );

  // Phase 7+++ — auto-sync once when the editor mounts. Silent for the
  // zero-changes case so we don't spam toasts on every navigation.
  useEffect(() => {
    if (!libsLoaded || autoSynced) return;
    setAutoSynced(true);
    runSync({ silent: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [libsLoaded, autoSynced, runSync]);

  const dirty =
    originalSnapshot !==
    JSON.stringify({ mealPlan, arrivalTime, departureTime, targets });

  // Browser back/forward + tab close guard.
  useEffect(() => {
    const handler = (e) => {
      if (!dirty) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  // Load libraries once.
  useEffect(() => {
    let cancelled = false;
    Promise.all([
      listRestaurants(),
      listThemeBuffets(),
      listLiveStations(),
      // Phase 7+++ — Standard Stations parallels Live Stations; missing
      // collection on older deployments shouldn't break editor load.
      listStandardStations().catch(() => []),
      // Phase 7++++ — Buffet Materials library (per-dish material picker).
      listBuffetMaterials().catch(() => []),
      // Phase 7+/Crew-Mess-Buffet — parallel Crew Mess Live Stations lib.
      listCrewMessLiveStations().catch(() => []),
      listCrewMessThemes(),
      listChefsSpecials(),
      listSpecialEvents(),
      listTargets(),
      listFestivals().catch(() => []),
      // Phase 16 (CO1/CO4) — Pizza catalog, Pizza sets, Bakery specials,
      // Chef's/Bakery special groups. All catch-empty by default.
      listPizzas(),
      listPizzaSets(),
      listBakerySpecials(),
      listChefsSpecialsGroups(),
      listBakerySpecialsGroups(),
      // Phase 16 (F13) — Carving catalog + bundles. Same plumbing as Pizza.
      listCarvings(),
      listCarvingSets(),
    ])
      .then(([r, tb, ls, ss, bm, cmls, cmt, cs, se, tg, fe,
              pz, pzs, bs, csg, bsg, cv, cvs]) => {
        if (cancelled) return;
        setLibs({
          restaurants: r,
          theme_buffets: tb,
          live_stations: ls,
          standard_stations: ss,
          buffet_materials: bm,
          crew_mess_live_stations: cmls,
          crew_mess_themes: cmt,
          chefs_specials: cs,
          special_events: se,
          targets: tg,
          festivals: fe,
          // Phase 16 (CO1/CO4)
          pizzas: pz,
          pizza_sets: pzs,
          bakery_specials: bs,
          chefs_specials_groups: csg,
          bakery_specials_groups: bsg,
          // Phase 16 (F13)
          carvings: cv,
          carving_sets: cvs,
        });
        setLibsLoaded(true);
      })
      .catch((e) => {
        toast({
          title: "Failed to load library data",
          description: e?.response?.data?.detail || e.message,
          variant: "destructive",
        });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Load the active day whenever it changes.
  const loadDay = useCallback(
    async (n) => {
      setLoading(true);
      try {
        const d = await getDay(voyage.id, n);
        setDay(d);
        const at = d.arrival_time || "";
        const dt = d.departure_time || "";
        const mp = d.meal_plan || EMPTY_MEAL_PLAN;
        setArrivalTime(at);
        setDepartureTime(dt);
        setMealPlan(mp);
        const tgs = voyage.targets || [];
        setTargets(tgs);
        setOriginalSnapshot(
          JSON.stringify({ mealPlan: mp, arrivalTime: at, departureTime: dt, targets: tgs }),
        );
      } catch (e) {
        toast({
          title: "Failed to load day",
          description: e?.response?.data?.detail || e.message,
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    },
    [voyage.id, voyage.targets],
  );

  useEffect(() => {
    loadDay(safeDay);
  }, [safeDay, loadDay]);

  // Phase-5 ref 5 — load the full voyage meal-plan once on mount so we can
  // detect duplicate menu rotations ACROSS days. Refreshes after every save
  // (see handleSave).
  useEffect(() => {
    refreshFullMealPlan();
  }, [refreshFullMealPlan]);

  // Phase 14 — fetch the voyage's ship buffet-lines once on mount. The
  // resolver picks the right ship by voyage.ship_id (or ship_name) so
  // even a voyage on a different ship gets its own list of lines.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await import("@/apiClient").then((m) => m.default.get(
          `/voyages/${voyage.id}/buffet-lines`,
        ));
        if (cancelled) return;
        setLinesLib(r.data?.lines || []);
      } catch (e) {
        // Resolver failure shouldn't break the editor; just leave Unassigned.
        if (!cancelled) setLinesLib([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [voyage.id]);

  // Live duplicate map — derived from the persisted full meal plan with
  // the currently-edited day's mealPlan swapped in. This way the duplicate
  // highlighting tracks the user's edits before they hit "Save Day".
  const liveDays = useMemo(() => {
    if (!Array.isArray(fullMealPlan) || fullMealPlan.length === 0) return [];
    return fullMealPlan.map((d) =>
      d.day_number === safeDay
        ? { ...d, meal_plan: mealPlan }
        : d,
    );
  }, [fullMealPlan, safeDay, mealPlan]);

  const menuDuplicates = useMemo(
    () => computeMenuDuplicates(liveDays, libs.restaurants),
    [liveDays, libs.restaurants],
  );
  // Phase 7+++ — voyage-wide Chef's Special duplicates. Same map shape
  // (Map<normName, dayNumber[]>) replaces the same-day-only check that
  // used to live inside RegularRestaurantsTable.
  const chefSpecialDuplicates = useMemo(
    () => computeChefSpecialDuplicates(liveDays),
    [liveDays],
  );
  const duplicateDayNumbers = useMemo(() => {
    const union = new Set(daysWithDuplicateMenus(menuDuplicates));
    daysWithDuplicateChefSpecials(chefSpecialDuplicates).forEach((n) =>
      union.add(n),
    );
    return union;
  }, [menuDuplicates, chefSpecialDuplicates]);

  const selectDay = (n) =>
    setSearchParams((sp) => {
      const next = new URLSearchParams(sp);
      next.set("day", String(n));
      return next;
    });

  const handleSave = async () => {
    setSaving(true);
    try {
      // 1. Voyage-level targets (only if changed).
      const origTargetsStr = JSON.stringify(voyage.targets || []);
      if (JSON.stringify(targets) !== origTargetsStr) {
        await updateVoyage(voyage.id, { targets });
      }

      // 2. arrival/departure on the day.
      const dayPatch = {};
      if (arrivalTime !== (day?.arrival_time || ""))
        dayPatch.arrival_time = arrivalTime || null;
      if (departureTime !== (day?.departure_time || ""))
        dayPatch.departure_time = departureTime || null;
      if (Object.keys(dayPatch).length) {
        await patchDay(voyage.id, safeDay, dayPatch);
      }

      // 3. meal-plan body.
      // Phase-5 ref 6 — sanitize the payload before sending so a chef's
      // special toggle that's ON but with no value doesn't 422 the save.
      // The original `mealPlan` state is left untouched on the user's
      // screen — only the wire payload is cleaned.
      const { payload, autoDisabledRestaurants } =
        sanitizeMealPlanForSave(mealPlan);
      const saved = await putMealPlan(voyage.id, safeDay, payload);
      setDay(saved);
      const at = saved.arrival_time || "";
      const dt = saved.departure_time || "";
      const mp = saved.meal_plan || EMPTY_MEAL_PLAN;
      setArrivalTime(at);
      setDepartureTime(dt);
      // Sync local state to whatever the server persisted (which has the
      // sanitized chef's_special.enabled=false for any rows we cleaned).
      setMealPlan(mp);
      setOriginalSnapshot(
        JSON.stringify({ mealPlan: mp, arrivalTime: at, departureTime: dt, targets }),
      );
      setLastSavedAt(new Date());
      // Phase-5 ref 5 — refresh the voyage-wide meal-plan cache so the
      // duplicate-menu detection has the freshly-saved day in its view.
      refreshFullMealPlan();
      toast({
        title: "Day saved",
        description: `Day ${safeDay} meal plan + targets persisted.`,
      });
      // Phase-5 ref 6 — surface what we silently cleaned so the user
      // knows the toggle on those restaurants is now off.
      if (autoDisabledRestaurants.length > 0) {
        toast({
          title: "Chef's Special toggle reset",
          description:
            `${autoDisabledRestaurants.length} restaurant(s) had Chef's ` +
            `Special toggled on but no value picked — they were saved as ` +
            `off: ${autoDisabledRestaurants.join(", ")}.`,
        });
      }
      // Phase 15 (F3) — soft yellow warning toast if the just-saved day
      // re-uses a Live Station already booked on another day of this
      // voyage. The save itself is never blocked — duplicates are
      // intentional sometimes (special menu repeat). Backend endpoint:
      // GET /api/voyages/{vid}/live-station-usage.
      try {
        const { usage } = await listVoyageLiveStationUsage(voyage.id);
        const offenders = [];
        for (const [lsId, occs] of Object.entries(usage || {})) {
          if (!Array.isArray(occs) || occs.length < 2) continue;
          // Only warn when one of the duplicate occurrences is on the
          // day we just saved — otherwise we'd nag on every save.
          if (!occs.some((o) => Number(o.day_number) === Number(safeDay))) {
            continue;
          }
          const name =
            (occs.find((o) => (o.name || "").trim())?.name)
            || `Live Station ${lsId.slice(0, 6)}`;
          const where = occs
            .map((o) => `Day ${o.day_number} ${o.meal || ""}`.trim())
            .join(" · ");
          offenders.push(`${name} (${where})`);
        }
        if (offenders.length > 0) {
          toast({
            title: "⚠ Duplicate Live Station usage",
            description:
              `This voyage uses the same Live Station on more than one ` +
              `day: ${offenders.join("; ")}. ` +
              `Saved as-is — change it if this was unintentional.`,
            // Soft warning — yellow tint via destructive=false. The
            // toast sticks around long enough for the user to read.
            duration: 10000,
            className:
              "border-amber-400 bg-amber-50 text-amber-900",
            "data-testid": "live-station-duplicate-warning",
          });
        }
      } catch (_usageErr) {
        // Soft-fail: never block save on a usage probe failure.
      }
      // Phase 16 (F8) — duplicate menu rotation across the voyage. Same
      // pattern as the F3 live-station probe: GET /api/voyages/:id/menu-
      // usage, surface a soft toast if any menu_id appears on ≥2 days
      // AND one of those days is the just-saved day. Save never blocks.
      try {
        const { usage: menuUsage } = await listVoyageMenuUsage(voyage.id);
        const dupes = [];
        for (const [_key, occs] of Object.entries(menuUsage || {})) {
          if (!Array.isArray(occs) || occs.length < 2) continue;
          if (!occs.some((o) => Number(o.day_number) === Number(safeDay))) continue;
          const name = occs.find((o) => (o.menu_name || "").trim())?.menu_name
                       || "(unnamed menu)";
          const where = occs.map(
            (o) => `Day ${o.day_number} ${o.meal || ""} (${o.restaurant_name || "?"})`.trim()
          ).join(" · ");
          dupes.push(`${name} (${where})`);
        }
        if (dupes.length > 0) {
          toast({
            title: "⚠ Menu used on more than one day",
            description: `${dupes.join("; ")}. ` +
              "Saved as-is — keep both if this is intentional, " +
              "or switch one of the days.",
            duration: 10000,
            className: "border-amber-400 bg-amber-50 text-amber-900",
            "data-testid": "menu-duplicate-warning",
          });
        }
      } catch (_menuUsageErr) {
        // Soft-fail.
      }
      // Phase 16 (F9) — soft warning when a Special Event was added to
      // this day but has no start/end time. Scans the local snapshot
      // (no extra round-trip needed). Never blocks save.
      try {
        const eventsOnDay = (mealPlan?.special_events) || [];
        const timeless = eventsOnDay.filter((e) => {
          if (!e || !e.name) return false;
          const hasStart = (e.start || e.start_time || "").trim();
          const hasEnd = (e.end || e.end_time || "").trim();
          return !hasStart && !hasEnd;
        });
        if (timeless.length > 0) {
          toast({
            title: "⏱ Event(s) without a time",
            description:
              `${timeless.map((e) => `'${e.name}'`).join(", ")} ` +
              "has no start/end time set. Saved as-is — add times " +
              "later if guests need them on the PDF.",
            duration: 8000,
            className: "border-amber-400 bg-amber-50 text-amber-900",
            "data-testid": "timeless-event-warning",
          });
        }
      } catch (_evErr) {
        // Soft-fail.
      }
    } catch (e) {
      toast({
        title: "Save failed",
        description: formatValidationError(e),
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleResetEvents = async () => {
    if (
      dirty &&
      !window.confirm(
        "This will overwrite your in-progress events with the ship defaults. Continue?",
      )
    ) {
      return;
    }
    setResetting(true);
    try {
      await eventsFromDefaults(voyage.id, safeDay);
      await loadDay(safeDay);
      toast({
        title: "Events reset",
        description: "Defaults loaded from daily events library.",
      });
    } catch (e) {
      toast({
        title: "Reset failed",
        description: e?.response?.data?.detail || e.message,
        variant: "destructive",
      });
    } finally {
      setResetting(false);
    }
  };

  const handleCancel = () => {
    if (dirty && !window.confirm("Discard unsaved changes?")) return;
    loadDay(safeDay);
  };

  if (!libsLoaded || loading || !day) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const status = computeStatus(mealPlan);

  return (
    <div className="space-y-4 pb-24" data-testid="meal-plan-editor">
      {/* Day navigator + Export PDF */}
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0">
          <DayNavigator
            days={voyage.days || []}
            activeDay={safeDay}
            onSelect={selectDay}
            dirty={dirty}
            daysWithDuplicates={duplicateDayNumbers}
          />
        </div>
        {onExportPdf && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              // B7 — gate the destructive-looking action behind a confirm.
              // The sync IS non-destructive (it only APPENDS rows), but
              // the user just lived through a data-loss scare around this
              // exact button. Explicit consent + an automatic pre_sync
              // auto-backup means the next click can be undone in one
              // press from the 🕐 Backups panel.
              if (window.confirm(
                "Sync from library fills in any newly-enabled "
                + "restaurants on every day of this voyage. Your typed "
                + "dishes, menus, times and theme choices are preserved.\n\n"
                + "A safety auto-backup is taken first — you can roll "
                + "back from the 🕐 Backups panel if anything looks off.\n\n"
                + "Continue?"
              )) {
                runSync({ silent: false });
              }
            }}
            disabled={syncing}
            className="shrink-0"
            data-testid="meal-plan-sync-btn"
            title="Append newly enabled library restaurants to every day of this voyage"
          >
            {syncing ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            Sync from library
          </Button>
        )}
        {onExportPdf && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                disabled={exporting}
                className="shrink-0"
                data-testid="export-pdf-btn-mealplan"
              >
                {exporting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Download className="h-4 w-4" />
                )}
                Export PDF
                <ChevronDown className="h-3.5 w-3.5 opacity-70" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64">
              <DropdownMenuLabel className="text-xs uppercase tracking-wide text-muted-foreground">
                PDF Layout
              </DropdownMenuLabel>
              <DropdownMenuItem
                onClick={() => onExportPdf("per_day")}
                data-testid="export-pdf-per-day-btn-mealplan"
                className="gap-2"
              >
                <FileText className="h-4 w-4" />
                <div className="flex flex-col">
                  <span className="text-sm font-medium">Per-day PDF</span>
                  <span className="text-xs text-muted-foreground leading-tight">
                    One A2 landscape page per voyage day.
                  </span>
                </div>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => onExportPdf("single_page")}
                data-testid="export-pdf-single-page-btn-mealplan"
                className="gap-2"
              >
                <LayoutGrid className="h-4 w-4" />
                <div className="flex flex-col">
                  <span className="text-sm font-medium">All in one page</span>
                  <span className="text-xs text-muted-foreground leading-tight">
                    Every day as a column on ONE A2 landscape page.
                  </span>
                </div>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      {/* Day header card */}
      <div
        className="sticky top-[68px] z-10 rounded-lg border bg-card p-3 shadow-sm"
        data-testid="day-header-card"
      >
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-bold">Day {day.day_number}</span>
              <span className="text-sm text-muted-foreground">·</span>
              <span className="font-mono text-sm">{day.date}</span>
              <span className="text-sm text-muted-foreground">·</span>
              {day.port_or_sea === "Port" ? (
                <span className="text-sm font-semibold text-sky-700">
                  Port: {day.port_name || "—"}
                </span>
              ) : (
                <span className="text-sm font-semibold text-slate-700">
                  Sea Day
                </span>
              )}
              <Badge
                variant="outline"
                className={status.color}
                data-testid="day-status-badge"
              >
                {status.label}
              </Badge>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1.5">
              <Label className="text-xs">Arrival</Label>
              <Input
                value={arrivalTime}
                onChange={(e) => setArrivalTime(e.target.value)}
                placeholder="HH:MM"
                className="h-8 w-20 font-mono text-xs"
                data-testid="arrival-time-input"
              />
            </div>
            <div className="flex items-center gap-1.5">
              <Label className="text-xs">Departure</Label>
              <Input
                value={departureTime}
                onChange={(e) => setDepartureTime(e.target.value)}
                placeholder="HH:MM"
                className="h-8 w-20 font-mono text-xs"
                data-testid="departure-time-input"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Hotfix 4 — Regular Restaurants (collapsible) */}
      <CollapsibleSection
        sectionKey="regular_restaurants"
        title="Regular Restaurants"
        testIdPrefix="day-section-regular_restaurants"
      >
        <SectionErrorBoundary label="Regular Restaurants section" testId="regular-restaurants">
        <RegularRestaurantsTable
          value={mealPlan.regular_restaurants}
          onChange={(regular_restaurants) =>
            setMealPlan((mp) => ({ ...mp, regular_restaurants }))
          }
          restaurantsLib={libs.restaurants}
          chefsSpecialsLib={libs.chefs_specials}
          chefsSpecialsGroupsLib={libs.chefs_specials_groups}
          festivalsLib={libs.festivals}
          menuDuplicates={menuDuplicates}
          chefSpecialDuplicates={chefSpecialDuplicates}
          currentDayNumber={safeDay}
        />
        </SectionErrorBoundary>
      </CollapsibleSection>

      {/* Hotfix 4 — Specialty Restaurants (collapsible) */}
      <CollapsibleSection
        sectionKey="specialty_restaurants"
        title="Specialty Restaurants"
        testIdPrefix="day-section-specialty_restaurants"
      >
        <SpecialtyRestaurantsTable
          value={mealPlan.specialty_restaurants}
          onChange={(specialty_restaurants) =>
            setMealPlan((mp) => ({ ...mp, specialty_restaurants }))
          }
          restaurantsLib={libs.restaurants}
          festivalsLib={libs.festivals}
          dayNumber={safeDay}
        />
      </CollapsibleSection>

      {/* Hotfix 4 — Buffets (collapsible). The inner BuffetsBlock
          renders one OvcBlock per buffet-type restaurant — each with
          its own internal "<Name> Buffet — Independent themes per
          meal" subtitle. We wrap them with a single outer collapsible
          so the whole stack can be hidden in one click. */}
      <CollapsibleSection
        sectionKey="buffets"
        title="Ocean View Cafe Buffet — Independent themes per meal"
        testIdPrefix="day-section-buffets"
      >
        {/* Phase 14 — per-day buffet print buttons (Detailed + Summary).
            Lives right above the BuffetsBlock for fast OVC team access. */}
        <div className="mb-3 flex justify-end">
          <BuffetDayPrintButtons
            voyageId={voyage.id}
            dayNumber={safeDay}
          />
        </div>
        <SectionErrorBoundary label="OVC Buffets section" testId="ovc-buffets">
        <BuffetsBlock
          value={mealPlan.buffets}
          onChange={(buffets) => setMealPlan((mp) => ({ ...mp, buffets }))}
          themeBuffetsLib={libs.theme_buffets}
          liveStationsLib={libs.live_stations}
          standardStationsLib={libs.standard_stations}
          materialsLib={libs.buffet_materials}
          festivalsLib={libs.festivals}
          restaurantsLib={libs.restaurants}
          dayNumber={safeDay}
          linesLib={linesLib}
          pizzasLib={libs.pizzas}
          pizzaSetsLib={libs.pizza_sets}
          carvingsLib={libs.carvings}
          carvingSetsLib={libs.carving_sets}
          chefsSpecialsLib={libs.chefs_specials}
          chefsSpecialsGroupsLib={libs.chefs_specials_groups}
          bakerySpecialsLib={libs.bakery_specials}
          bakerySpecialsGroupsLib={libs.bakery_specials_groups}
        />
        </SectionErrorBoundary>
      </CollapsibleSection>

      {/* Hotfix 4 — Crew Mess (collapsible). Phase 13 — on Day 1 we
          render any of the 3 embarkation meal blocks the user has
          opted into via the Crew Mess Buffet library form. From Day 2
          onwards those slots are hidden from the editor entirely. */}
      <CollapsibleSection
        sectionKey="crew_mess"
        title="Crew Mess Buffet"
        testIdPrefix="day-section-crew_mess"
      >
        <SectionErrorBoundary label="Crew Mess section" testId="crew-mess">
        <OvcBlock
          value={(() => {
            // Pre-populate empty embark blocks on Day 1 so OvcBlock
            // surfaces them via the `meals` prop even if the user
            // hasn't touched them yet. Days 2+ pass through unchanged.
            if (safeDay !== 1) return mealPlan.crew_mess;
            const base = mealPlan.crew_mess || {};
            const next = { ...base };
            for (const em of ["embarkation_breakfast", "embarkation_lunch", "embarkation_dinner"]) {
              if (next[em] === undefined || next[em] === null) {
                next[em] = {
                  theme_buffet_id: null,
                  theme_buffet_name: "",
                  hot_kitchen: [],
                  cold_kitchen: [],
                  pastry: [],
                  lines: [],
                  live_station: {
                    enabled: false, live_station_id: null, name: "",
                    description: "", dishes: [],
                  },
                  festivals: [],
                };
              }
            }
            return next;
          })()}
          onChange={(nextCrew) => {
            // Strip embark blocks from the patch on Day 2+ so the
            // editor never accidentally persists them through Day-N>1.
            const patch = { ...nextCrew };
            if (safeDay !== 1) {
              delete patch.embarkation_breakfast;
              delete patch.embarkation_lunch;
              delete patch.embarkation_dinner;
            }
            setMealPlan((mp) => ({ ...mp, crew_mess: patch }));
          }}
          themeBuffetsLib={libs.theme_buffets}
          liveStationsLib={libs.crew_mess_live_stations}
          standardStationsLib={libs.standard_stations}
          materialsLib={libs.buffet_materials}
          festivalsLib={libs.festivals}
          linesLib={linesLib}
          pizzasLib={libs.pizzas}
          pizzaSetsLib={libs.pizza_sets}
          chefsSpecialsLib={libs.chefs_specials}
          chefsSpecialsGroupsLib={libs.chefs_specials_groups}
          bakerySpecialsLib={libs.bakery_specials}
          bakerySpecialsGroupsLib={libs.bakery_specials_groups}
          title="Crew Mess Buffet — Independent themes per meal"
          testId="crew-mess-buffet-block"
          buffetId="crew-mess"
          meals={
            safeDay === 1
              ? [
                  "embarkation_breakfast",
                  "breakfast",
                  "lunch",
                  "embarkation_lunch",
                  "embarkation_dinner",
                  "dinner",
                ]
              : ["breakfast", "lunch", "dinner"]
          }
        />
        </SectionErrorBoundary>
      </CollapsibleSection>

      {/* Hotfix 4 — Events (collapsible). */}
      <CollapsibleSection
        sectionKey="events"
        title="Events"
        testIdPrefix="day-section-events"
      >
        <EventsTable
          value={mealPlan.events}
          onChange={(events) => setMealPlan((mp) => ({ ...mp, events }))}
          onResetDefaults={handleResetEvents}
          resetting={resetting}
          dailyEventsLib={libs.daily_events}
        />
      </CollapsibleSection>

      {/* Hotfix 4 — Special Events (collapsible). */}
      <CollapsibleSection
        sectionKey="special_events"
        title="Special Events"
        testIdPrefix="day-section-special_events"
      >
        <SpecialEventsPicker
          value={mealPlan.special_events}
          onChange={(special_events) =>
            setMealPlan((mp) => ({ ...mp, special_events }))
          }
          specialEventsLib={libs.special_events}
        />
      </CollapsibleSection>

      {/* Hotfix 4 — Canapé Function (collapsible). Shares the same
          `special_events` array but only edits entries where
          `is_canape_function`. */}
      <CollapsibleSection
        sectionKey="canape_function"
        title="Canapé Function"
        testIdPrefix="day-section-canape_function"
      >
        <CanapeFunctionsBlock
          value={mealPlan.special_events}
          onChange={(special_events) =>
            setMealPlan((mp) => ({ ...mp, special_events }))
          }
        />
      </CollapsibleSection>
      {/* Sticky save footer */}
      <div className="fixed inset-x-0 bottom-0 z-30 border-t bg-background/95 px-6 py-3 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-2">
          <div
            className="text-xs text-muted-foreground"
            data-testid="last-saved-label"
          >
            {dirty ? (
              <span className="font-semibold text-amber-600">
                Unsaved changes
              </span>
            ) : lastSavedAt ? (
              <>Last saved at {lastSavedAt.toLocaleTimeString()}</>
            ) : (
              <>No changes yet</>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleCancel}
              disabled={!dirty || saving}
              data-testid="cancel-meal-plan-btn"
            >
              <RotateCcw className="h-3.5 w-3.5" /> Discard
            </Button>
            <Button
              size="sm"
              onClick={handleSave}
              disabled={!dirty || saving}
              data-testid="save-meal-plan-btn"
            >
              {saving ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Save className="h-3.5 w-3.5" />
              )}
              Save Day
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
